import React, { useMemo, useState } from "react";

import { connect, useSelector } from "react-redux";

import SecaoDeConquista from "./SecaoDeConquista";
import { TIPOS_DE_CONQUISTA } from "./tiposDeConquista";
import ProtectedRoute from "../../../components/ProtectedRoute";
import "./styles.css";

/**
 * MINHAS CONQUISTAS — o hub do aluno, com uma seção por TIPO de conquista.
 *
 * A página não sabe o que é medalha nem o que é carta: ela percorre
 * `tiposDeConquista.js`, dá a cada tipo a mesma moldura (`SecaoDeConquista`) e guarda
 * duas coisas — quais estão abertas e o resumo que cada uma reportou. Acrescentar um
 * tipo (troféu de turma, selo de projeto) é uma entrada no registro; nada aqui muda.
 *
 * Foi assim que se resolveu o problema real: enquanto cada tipo trazia o próprio
 * cabeçalho, a página tinha um dialeto por seção, e o próximo tipo inventaria um
 * terceiro. A moldura única é o que faz "futuros tipos de conquista" caberem sem
 * redesenho.
 *
 * A PÁGINA NÃO TEM ESTADO DE CARREGAMENTO, e isso é decisão: cada seção busca do
 * próprio endpoint, falha e se recupera por conta própria. Antes o `useState` das
 * medalhas vivia aqui e mandava na página inteira — uma falha em `achievements/me`
 * deixava tudo em spinner, e a coleção de cartas não aparecia por causa de um erro que
 * não era dela.
 *
 * TODAS AS SEÇÕES NASCEM ABERTAS. O aluno entra para VER o que tem; abrir uma por uma
 * seria cobrar um clique para chegar ao conteúdo da própria página. O recolher existe
 * para o caminho inverso — passar por cima de uma seção grande (a coleção tem dezenas
 * de cartas) para chegar à de baixo.
 */
function Achievements(props) {
  const token = useSelector(state => state.user.token);

  /* Os RECOLHIDOS, e não os abertos: assim um tipo novo no registro nasce aberto sem
     ninguém se lembrar de acrescentá-lo a uma lista de iniciais. */
  const [recolhidas, setRecolhidas] = useState({});

  /* O resumo que cada seção reportou, por id — é o "3 de 12" que aparece no cabeçalho.
     Mora aqui porque quem desenha o cabeçalho é a moldura, e quem sabe os números é o
     conteúdo. */
  const [resumos, setResumos] = useState({});

  const alternar = id =>
    setRecolhidas(atual => ({ ...atual, [id]: !atual[id] }));

  /* UM `onResumo` POR TIPO, criado UMA vez. Os dois cuidados aqui não são zelo
     preventivo: o conteúdo recebe `onResumo` como dependência de um `useEffect`, então

     1. função nova a cada renderização da página faz o efeito do conteúdo rodar de
        novo, que chama `onResumo`, que renderiza a página — laço. Daí o `useMemo` com
        dependências vazias, que é legítimo porque o registro é constante em módulo e
        `setResumos` é estável;
     2. gravar um resumo IGUAL ao que já está lá renderiza sem motivo, e é o outro lado
        do mesmo laço. Devolver `atual` inalterado é o que o React lê como "nada
        mudou". */
  const avisosDeResumo = useMemo(() => {
    const porTipo = {};

    TIPOS_DE_CONQUISTA.forEach(tipo => {
      porTipo[tipo.id] = resumo =>
        setResumos(atual => {
          const antes = atual[tipo.id];
          if (
            antes &&
            antes.conquistadas === resumo.conquistadas &&
            antes.total === resumo.total
          ) {
            return atual;
          }
          return { ...atual, [tipo.id]: resumo };
        });
    });

    return porTipo;
  }, []);

  return (
    <div className="container conquistas">
      <header className="conquistas__cabecalho">
        <h4 className="conquistas__titulo">Minhas Conquistas</h4>
        <p className="conquistas__subtitulo">
          Tudo o que você já ganhou estudando — e o que ainda falta para
          colecionar.
        </p>
      </header>

      {TIPOS_DE_CONQUISTA.map(tipo => (
        <SecaoDeConquista
          key={tipo.id}
          id={tipo.id}
          titulo={tipo.titulo}
          descricao={tipo.descricao}
          Icone={tipo.Icone}
          resumo={resumos[tipo.id]}
          aberta={!recolhidas[tipo.id]}
          onAlternar={() => alternar(tipo.id)}
        >
          <tipo.Conteudo token={token} onResumo={avisosDeResumo[tipo.id]} />
        </SecaoDeConquista>
      ))}

      <ProtectedRoute routeId={props.currentRoute} />
    </div>
  );
}

export default connect()(Achievements);
